import type { CreateTemplateOptions } from '@domains/subscription/priv/Templates/TemplateForm'
import { createCustomTheme } from '@domains/subscription/theme'
import {
  Autocomplete,
  Button,
  Dialog,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ThemeProvider,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import {
  BillingFrequency,
  type Country,
  type Currency,
  type Eligibility,
  type Partner,
  type PricePlanTemplate,
  SupportType,
} from '../types'

interface Option {
  label: string
  value: string
}

interface EditTemplateFormValues {
  name: string
  externalName: string
  eligibility: Eligibility
  currency: string
  partner: string
  country: { label: string; value: string }
}

export interface EditTemplateFormData {
  name: string
  externalName: string
  eligibility: Eligibility
}

export interface EditTemplateProps {
  template: PricePlanTemplate
  open: boolean
  onClose: () => void
  onSave: (data: EditTemplateFormData) => Promise<void>
  options: CreateTemplateOptions | undefined
}

const billingIntervalOptions = [
  { label: 'Monthly', value: BillingFrequency.MONTHLY },
  { label: 'Yearly', value: BillingFrequency.YEARLY },
]

export const EditTemplate = ({ template, open, onClose, onSave, options }: EditTemplateProps) => {
  const [isLoading, setIsLoading] = useState(false)

  const partnerOptions = options?.partners ?? []
  const countryOptions = options?.countries ?? []
  const currencyOptions = options?.currencies ?? []

  const partnerOptionsWithAll = [
    { label: 'All Partners', value: SupportType.ALL },
    ...partnerOptions.map((partner) => ({ label: partner, value: partner })),
  ]

  const countryOptionsWithAll = [{ label: 'All Countries', value: SupportType.ALL }, ...countryOptions]

  const getCountryLabel = (value: string) => {
    return countryOptionsWithAll.find((option) => option.value === value)?.label ?? value
  }

  const getPartnerLabel = (value: string) => {
    return partnerOptionsWithAll.find((option) => option.value === value)?.label ?? value
  }

  const form = useForm<EditTemplateFormValues>({
    values: {
      name: template.name,
      externalName: template.externalName,
      eligibility: template.eligibility,
      country: {
        label: getCountryLabel(template.eligibility.countries?.values?.[0] ?? SupportType.ALL),
        value: template.eligibility.countries?.values?.[0] ?? SupportType.ALL,
      },
      partner: template.eligibility.partners?.values?.[0] ?? SupportType.ALL,
      currency: template.eligibility.currencies?.values?.[0] ?? SupportType.ALL,
    },
  })

  const handleSave = (values: EditTemplateFormValues) => {
    setIsLoading(true)

    onSave({
      name: values.name,
      externalName: values.externalName,
      eligibility: values.eligibility,
    })
      .then(() => {
        onClose()
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <ThemeProvider theme={createCustomTheme()}>
        <form onSubmit={form.handleSubmit(handleSave, console.log)}>
          <Stack
            sx={{
              gap: 3,
              padding: 3,
              minWidth: 400,
            }}
          >
            <Typography variant="h2">Edit template</Typography>
            <TextField
              fullWidth
              id="edit-template-name"
              label="Template Name"
              variant="outlined"
              {...form.register('name', { required: true })}
            />
            <TextField
              fullWidth
              id="edit-template-external-name"
              label="External Name"
              variant="outlined"
              {...form.register('externalName', { required: true })}
            />
            <Stack
              direction="row"
              sx={{
                gap: 2,
              }}
            >
              <FormControl fullWidth>
                <InputLabel id="edit-template-currencies-label">Currencies</InputLabel>
                <Controller
                  control={form.control}
                  name="currency"
                  render={({ field }) => (
                    <Select
                      id="edit-template-currencies"
                      label="Currencies"
                      variant="outlined"
                      labelId="edit-template-currencies-label"
                      value={field.value}
                      onChange={(event) => {
                        const value = event.target.value

                        field.onChange(value)

                        if (value === SupportType.ALL) {
                          form.setValue('eligibility.currencies', {
                            type: SupportType.ALL,
                            values: undefined,
                          })
                        } else {
                          form.setValue('eligibility.currencies', {
                            type: SupportType.SPECIFIC,
                            values: [value as Currency],
                          })
                        }
                      }}
                    >
                      <MenuItem value={SupportType.ALL}>All Currencies</MenuItem>
                      {currencyOptions.map((currency) => (
                        <MenuItem key={currency} value={currency}>
                          {currency}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="edit-template-billing-interval-label">Billing Interval</InputLabel>
                <Controller
                  control={form.control}
                  name="eligibility.billingFrequency"
                  rules={{ required: true }}
                  render={({ field }) => (
                    <Select
                      id="edit-template-billing-interval"
                      label="Billing Interval"
                      variant="outlined"
                      labelId="edit-template-billing-interval-label"
                      value={field.value ?? ''}
                      onChange={(event) => field.onChange(event.target.value as BillingFrequency)}
                    >
                      {billingIntervalOptions.map(({ label, value }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </FormControl>
            </Stack>
            <Stack
              direction="row"
              sx={{
                gap: 2,
              }}
            >
              <Controller
                control={form.control}
                name="partner"
                render={({ field }) => (
                  <Autocomplete
                    fullWidth
                    id="edit-template-partners"
                    options={partnerOptionsWithAll}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    defaultValue={partnerOptionsWithAll[0] as Option}
                    disableClearable
                    value={{ value: field.value, label: getPartnerLabel(field.value) }}
                    onChange={(event, option) => {
                      const value = option.value as Partner

                      form.setValue('partner', value)

                      if (value === SupportType.ALL) {
                        form.setValue('eligibility.partners', {
                          type: SupportType.ALL,
                          values: undefined,
                        })
                      } else {
                        form.setValue('eligibility.partners', {
                          type: SupportType.SPECIFIC,
                          values: [value],
                        })
                      }
                    }}
                    renderInput={(params) => <TextField {...params} label="Partners" variant="outlined" />}
                  />
                )}
              />
              <Controller
                control={form.control}
                name="country"
                render={({ field }) => (
                  <Autocomplete
                    fullWidth
                    id="edit-template-countries"
                    options={countryOptionsWithAll}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    defaultValue={countryOptionsWithAll[0] as Option}
                    disableClearable
                    value={field.value}
                    onChange={(event, option) => {
                      const value = option.value as Country

                      form.setValue('country', { value, label: getCountryLabel(value) })

                      if (value === SupportType.ALL) {
                        form.setValue('eligibility.countries', {
                          type: SupportType.ALL,
                          values: undefined,
                        })
                      } else {
                        form.setValue('eligibility.countries', {
                          type: SupportType.SPECIFIC,
                          values: [value],
                        })
                      }
                    }}
                    renderInput={(params) => <TextField {...params} label="Countries" variant="outlined" />}
                  />
                )}
              />
            </Stack>
            {Object.keys(form.formState.errors).length > 0 && (
              <Typography variant="p3Regular" color="error" sx={{ whiteSpace: 'pre-wrap' }}>
                {Object.entries(form.formState.errors)
                  .map(([field, error]) => `${field}: ${error?.message ?? 'Invalid value'}`)
                  .join('\n')}
              </Typography>
            )}
            <Stack
              direction="row"
              sx={{
                justifyContent: 'flex-end',
                gap: 2,
              }}
            >
              <Button disabled={isLoading} size="medium" data-testid="cancel-edit-template" variant="tertiary" onClick={onClose}>
                Cancel
              </Button>
              <Button loading={isLoading} size="medium" data-testid="confirm-edit-template" type="submit" variant="contained">
                Save
              </Button>
            </Stack>
          </Stack>
        </form>
      </ThemeProvider>
    </Dialog>
  )
}
